/**
 * ChatInput - Input de mensagens com suporte a texto e ações
 */

import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import type { QuickMessageResponse } from '@troia-v3/types';
import { Icon } from '@/shared/icons'
import QuickMessagePicker from '@/modules/quick-messages/components/QuickMessagePicker';
import { CHAT_FILE_INPUT_ACCEPT } from '../utils/acceptedFileTypes';
import { chatDraftStore } from '../services/chatDraftStore';

export interface ChatInputHandle {
  /** Replace the composer text (used to insert a quick message). */
  setText: (text: string) => void;
  /** Focus the textarea. */
  focus: () => void;
}

interface ChatInputProps {
  conversationId: string;
  onSendMessage: (message: string) => void;
  disabled?: boolean;
  placeholder?: string;
  onFilesSelected?: (files: FileList) => void;
  onVoiceRecord?: () => void;
  hasAttachments?: boolean;
  /** Open the quick-messages management drawer. */
  onOpenQuickMessages?: () => void;
  /** Quick messages available for the "/" picker. */
  quickMessages?: QuickMessageResponse[];
  /** Insert the selected quick message into the composer (text + staged media). */
  onSelectQuickMessage?: (qm: QuickMessageResponse) => void;
  /** Abre o seletor de localização (menu do clipe — spec 2026-08-19). */
  onOpenLocationPicker?: () => void;
  /** Abre o seletor de contato (menu do clipe — spec 2026-08-19). */
  onOpenContactPicker?: () => void;
  /** Abre o criador de enquete (só canais QR Code — Meta não suporta). */
  onOpenPollCreator?: () => void;
}

const ChatInput = forwardRef<ChatInputHandle, ChatInputProps>(({
  conversationId,
  onSendMessage,
  disabled = false,
  // Mockup: "Digite uma mensagem…" no desktop, "Mensagem…" no composer mobile
  placeholder = typeof window !== 'undefined' && window.matchMedia('(max-width: 1023px)').matches
    ? 'Mensagem...'
    : 'Digite uma mensagem...',
  onFilesSelected,
  onVoiceRecord,
  hasAttachments = false,
  onOpenQuickMessages,
  quickMessages = [],
  onSelectQuickMessage,
  onOpenLocationPicker,
  onOpenContactPicker,
  onOpenPollCreator,
}, ref) => {
  const [message, setMessage] = useState(() => chatDraftStore.getText(conversationId));
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Quick-messages "/" picker state
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerQuery, setPickerQuery] = useState('');

  // Menu de anexos do clipe (Arquivo / Localização / Contato — spec 2026-08-19).
  // Só vira menu quando o caller fornece os pickers; sem eles o clipe mantém o
  // comportamento antigo (file picker direto) — retrocompat com outros usos.
  const [attachMenuOpen, setAttachMenuOpen] = useState(false);
  const attachMenuAvailable = Boolean(onOpenLocationPicker || onOpenContactPicker || onOpenPollCreator);
  const [selectedIndex, setSelectedIndex] = useState(0);

  const filteredQuickMessages = useMemo(() => {
    if (!pickerOpen) return [];
    const q = pickerQuery.trim().toLowerCase();
    if (!q) return quickMessages;
    return quickMessages.filter(
      (qm) =>
        (qm.shortcut && qm.shortcut.toLowerCase().includes(q)) ||
        qm.title.toLowerCase().includes(q),
    );
  }, [pickerOpen, pickerQuery, quickMessages]);

  // Reset highlight whenever the filtered set changes.
  useEffect(() => {
    setSelectedIndex(0);
  }, [pickerQuery, pickerOpen]);

  const resizeTextarea = () => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    const maxHeight = 150;
    if (el.scrollHeight > maxHeight) {
      el.style.height = `${maxHeight}px`;
      el.style.overflowY = 'auto';
    } else {
      el.style.height = `${el.scrollHeight}px`;
      el.style.overflowY = 'hidden';
    }
  };

  // Rascunho restaurado no mount: ajustar a altura do textarea ao conteúdo.
  useEffect(() => {
    if (message) requestAnimationFrame(resizeTextarea);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const closePicker = () => {
    setPickerOpen(false);
    setPickerQuery('');
  };

  // Light dismiss (DS §Overlays dismissal): picker "/" é transiente — clicar/tocar
  // fora dele (e fora do composer) fecha. Esc já fecha via handleKeyDown do textarea.
  const composerRootRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!pickerOpen) return;
    const onPointerDown = (e: PointerEvent) => {
      const root = composerRootRef.current;
      if (root && !root.contains(e.target as Node)) {
        setPickerOpen(false);
        setPickerQuery('');
      }
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [pickerOpen]);

  // Light dismiss do menu de anexos — mesma política de overlay do picker "/"
  // (menu transiente fecha ao clicar fora; Esc fecha também).
  const attachMenuRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!attachMenuOpen) return;
    const onPointerDown = (e: PointerEvent) => {
      const menu = attachMenuRef.current;
      if (menu && !menu.contains(e.target as Node)) {
        setAttachMenuOpen(false);
      }
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setAttachMenuOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [attachMenuOpen]);

  useImperativeHandle(ref, () => ({
    setText: (text: string) => {
      setMessage(text);
      chatDraftStore.setText(conversationId, text);
      closePicker();
      requestAnimationFrame(() => {
        resizeTextarea();
        textareaRef.current?.focus();
      });
    },
    focus: () => textareaRef.current?.focus(),
  }));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Allow submit if there's text OR attachments
    if ((!message.trim() && !hasAttachments) || disabled) return;

    // ✅ Salvar mensagem antes de limpar
    const messageToSend = message.trim();

    // ✅ Limpar estado e resetar altura ANTES de enviar
    setMessage('');
    chatDraftStore.setText(conversationId, '');
    closePicker();
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.overflowY = 'hidden';
    }

    // ✅ Enviar mensagem (isso vai deixar o input disabled via mutation.isPending)
    onSendMessage(messageToSend);

    // ✅ Manter foco no textarea (mesmo que fique disabled temporariamente)
    if (textareaRef.current) {
      textareaRef.current.focus();
    }
  };

  const handleSelectQuickMessage = (qm: QuickMessageResponse) => {
    closePicker();
    // Text + staged media are applied by the parent (ChatBox) via the ref/state.
    onSelectQuickMessage?.(qm);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // While the "/" picker is open, keys drive the picker (not submit).
    if (pickerOpen) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex((i) => (filteredQuickMessages.length ? (i + 1) % filteredQuickMessages.length : 0));
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex((i) =>
          filteredQuickMessages.length ? (i - 1 + filteredQuickMessages.length) % filteredQuickMessages.length : 0,
        );
        return;
      }
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        const qm = filteredQuickMessages[selectedIndex];
        if (qm) {
          handleSelectQuickMessage(qm);
        } else {
          closePicker();
        }
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        closePicker();
        return;
      }
    }

    // Send on Enter (without Shift)
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e as unknown as React.FormEvent);
    }
  };

  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setMessage(value);
    chatDraftStore.setText(conversationId, value);

    // Open the quick-messages picker when the composer starts with "/".
    if (value.startsWith('/') && quickMessages.length > 0) {
      setPickerOpen(true);
      setPickerQuery(value.slice(1));
    } else if (pickerOpen) {
      closePicker();
    }

    resizeTextarea();
  };

  const handleAttachFile = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0 && onFilesSelected) {
      onFilesSelected(files);
      // Reset input para permitir selecionar o mesmo arquivo novamente
      e.target.value = '';
    }
  };

  const handleVoiceRecord = () => {
    if (onVoiceRecord) {
      onVoiceRecord();
    }
  };

  return (
    <div
      ref={composerRootRef}
      className="relative"
      style={{
        borderTop: '1px solid var(--theme-card-border)',
        backgroundColor: 'var(--theme-card-background)'
      }}
    >
      {/* Quick messages "/" picker (floats above the composer) */}
      {pickerOpen && (
        <QuickMessagePicker
          items={filteredQuickMessages}
          selectedIndex={selectedIndex}
          onSelect={handleSelectQuickMessage}
          onHover={setSelectedIndex}
        />
      )}

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept={CHAT_FILE_INPUT_ACCEPT}
        onChange={handleFileChange}
        className="hidden"
      />

      <form onSubmit={handleSubmit} className="w-full">
        <div className="flex items-end gap-2 px-3.5 py-3">
          {/* Left Actions — clipe abre menu (Arquivo/Localização/Contato) quando
              os pickers existem; sem eles, mantém o file picker direto */}
          <div className="relative flex-shrink-0" ref={attachMenuRef}>
            <button
              type="button"
              onClick={() => {
                // Nunca dois overlays transientes ao mesmo tempo: abrir o menu
                // fecha o picker "/" (achado da revisão adversarial 2026-08-19)
                closePicker();
                if (attachMenuAvailable) {
                  setAttachMenuOpen((open) => !open);
                } else {
                  handleAttachFile();
                }
              }}
              disabled={disabled}
              className="w-[38px] h-[38px] rounded-lg flex items-center justify-center flex-shrink-0 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ color: 'var(--theme-muted-text)' }}
              onMouseEnter={(e) => {
                if (!disabled) {
                  e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)';
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
              title="Anexar"
              data-testid="chat-input-attach"
            >
              <Icon name="paperclip" className="w-[18px] h-[18px]" />
            </button>

            {/* Menu de anexos — overlay transiente, tema claro fixo (regra da casa) */}
            {attachMenuOpen && (
              <div
                className="absolute bottom-[46px] left-0 rounded-xl overflow-hidden z-[100] min-w-[200px] py-1.5"
                style={{ backgroundColor: '#ffffff', border: '1px solid #e5e7eb', boxShadow: '0 12px 32px rgba(15, 23, 42, 0.16)' }}
                data-testid="chat-attach-menu"
              >
                <button
                  type="button"
                  className="w-full flex items-center gap-3 px-3.5 text-left text-sm transition-colors hover:bg-slate-50"
                  style={{ color: '#334155', minHeight: 44 }}
                  onClick={() => { setAttachMenuOpen(false); handleAttachFile(); }}
                  data-testid="chat-attach-file"
                >
                  <Icon name="file" className="w-[18px] h-[18px]" style={{ color: '#8b5cf6' }} />
                  Arquivo
                </button>
                {onOpenLocationPicker && (
                  <button
                    type="button"
                    className="w-full flex items-center gap-3 px-3.5 text-left text-sm transition-colors hover:bg-slate-50"
                    style={{ color: '#334155', minHeight: 44 }}
                    onClick={() => { setAttachMenuOpen(false); onOpenLocationPicker(); }}
                    data-testid="chat-attach-location"
                  >
                    <Icon name="map-pin" className="w-[18px] h-[18px]" style={{ color: '#8b5cf6' }} />
                    Localização
                  </button>
                )}
                {onOpenContactPicker && (
                  <button
                    type="button"
                    className="w-full flex items-center gap-3 px-3.5 text-left text-sm transition-colors hover:bg-slate-50"
                    style={{ color: '#334155', minHeight: 44 }}
                    onClick={() => { setAttachMenuOpen(false); onOpenContactPicker(); }}
                    data-testid="chat-attach-contact"
                  >
                    <Icon name="user" className="w-[18px] h-[18px]" style={{ color: '#8b5cf6' }} />
                    Contato
                  </button>
                )}
                {onOpenPollCreator && (
                  <button
                    type="button"
                    className="w-full flex items-center gap-3 px-3.5 text-left text-sm transition-colors hover:bg-slate-50"
                    style={{ color: '#334155', minHeight: 44 }}
                    onClick={() => { setAttachMenuOpen(false); onOpenPollCreator(); }}
                    data-testid="chat-attach-poll"
                  >
                    <Icon name="bar-chart" className="w-[18px] h-[18px]" style={{ color: '#8b5cf6' }} />
                    Enquete
                  </button>
                )}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={onOpenQuickMessages}
            disabled={disabled}
            className="w-[38px] h-[38px] rounded-lg flex items-center justify-center flex-shrink-0 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ color: 'var(--theme-muted-text)' }}
            onMouseEnter={(e) => {
              if (!disabled) {
                e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)';
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
            title="Mensagens rápidas"
            data-testid="chat-input-quick-messages"
          >
            <Icon name="zap" className="w-[18px] h-[18px]" />
          </button>

          {/* Textarea (pill field) */}
          <div className="relative flex-1 min-w-0 flex items-end">
            <textarea
              ref={textareaRef}
              value={message}
              onChange={handleTextChange}
              onKeyDown={handleKeyDown}
              disabled={disabled}
              placeholder={placeholder}
              rows={1}
              className="w-full border resize-none focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
              style={{
                maxHeight: '150px',
                minHeight: '40px',
                height: '40px',
                overflowY: 'hidden',
                lineHeight: '22px',
                padding: '8px 16px',
                borderRadius: '20px',
                backgroundColor: 'var(--theme-input-background)',
                borderColor: 'var(--theme-input-border)',
                color: 'var(--theme-input-text)'
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = 'var(--theme-input-focus)';
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = 'var(--theme-input-border)';
              }}
              data-testid="chat-input-textarea"
            />
            {!message && !disabled && (
              <span
                className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 hidden lg:block"
                style={{ color: 'var(--theme-muted-text)', fontSize: '11px' }}
              >
                / p/ atalhos
              </span>
            )}
          </div>

          {/* Right Actions */}
          {/* Microphone button */}
          <button
            type="button"
            onClick={handleVoiceRecord}
            disabled={disabled}
            className="w-[38px] h-[38px] rounded-lg flex items-center justify-center flex-shrink-0 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ color: 'var(--theme-muted-text)' }}
            onMouseEnter={(e) => {
              if (!disabled) {
                e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)';
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
            title="Gravar áudio"
            data-testid="chat-input-voice"
          >
            <Icon name="mic" className="w-[18px] h-[18px]" />
          </button>

          {/* Send button - primary circular action */}
          <button
            type="submit"
            disabled={disabled}
            className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            style={{
              backgroundColor: 'var(--theme-button-primary)',
              color: 'var(--theme-button-primary-text)'
            }}
            onMouseEnter={(e) => {
              if (!disabled) {
                e.currentTarget.style.backgroundColor = 'var(--theme-button-primary-hover)';
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--theme-button-primary)';
            }}
            title="Enviar mensagem"
            data-testid="chat-input-send"
          >
            <Icon name="send" className="w-[18px] h-[18px]" />
          </button>
        </div>

      </form>
    </div>
  );
});

ChatInput.displayName = 'ChatInput';

export default ChatInput;
